import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Pagination,
	TableView,
	TableViewNoResultConfig
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	createLocalDataSource,
	useSelection,
	useSortHandler
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	EMPTY_ARRAY,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { UserGroupOverviewModel } from "client/Api/UserGroupsManagement/userGroupsManagement";

const {
	Row,
	Cell
} = TableView;

export type UserColumnToShow = "all" | "active-direct";

export type UserGroupsOverviewSelectionProps = {
	selection: readonly UserGroupOverviewModel[];
	onSelectionChange(selection: readonly UserGroupOverviewModel[]): void;
} | {
	selection?: undefined;
	onSelectionChange?: undefined;
};

export type UserGroupsOverviewProps = {
	data: readonly UserGroupOverviewModel[];
	searchTerm: string;
	userColumnToShow?: UserColumnToShow;
	height?: "fill" | "stretch";
} & UserGroupsOverviewSelectionProps;

const useUserGroupDataSource = createLocalDataSource<UserGroupOverviewModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.name, searchTerm) ||
				contains(item.description, searchTerm),
		},
		hasPagination: true,
	})
	.getHook();

export const UserGroupOverview = (props: UserGroupsOverviewProps): ReactElement => {
	const {
		data,
		searchTerm,
		selection,
		onSelectionChange,
		userColumnToShow = "all",
		height
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<UserGroupOverviewModel>>({
		propertyName: "name",
		direction: DataSourceSortDirection.Ascending
	});
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { sortingFor } = useSortHandler<UserGroupOverviewModel>(sortingState, setSortingState);

	const { currentItems, pagination } = useUserGroupDataSource(data, {
		searchTerm,
		sorting: sortingState,
		pagination: {
			amountPerPage: 100,
			currentPageNumber
		},
	});

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCurrentPageNumber(0);
		},
		[searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const { selectionFor, multiSelection } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection ?? EMPTY_ARRAY,
			setSelectedItems: onSelectionChange ?? notAvailableSelectionHandler,
			hasMemorization: true,
			allItems: data
		}
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "user-group",
				title: localize("_NoGroupsYet"),
				message: localize("_NoGroupsHaveBeenAdded")
			};
		},
		[searchTerm]
	);

	return (
		<>
			<TableView
				multiSelectable={isNotNil(selection) && multiSelection}
				items={currentItems}
				itemKey="id"
				columns={[
					{
						title: localize("_Name"),
						sortable: sortingFor("name"),
						trackingId: "name"
					},
					userColumnToShow === "all" && {
						title: localize("_NumberOfUsers"),
						sortable: sortingFor("numberOfUsers"),
						trackingId: "number-of-users"
					},
					{
						title: localize("_Description"),
						sortable: sortingFor("description"),
						trackingId: "description"
					},
					userColumnToShow === "active-direct" && {
						title: localize("_ActiveUsers"),
						sortable: sortingFor("numberOfActiveNonDeletedDirectUsers"),
						trackingId: "active-users"
					}
				]}
				trackingId="user-group-overview"
				ref={tableRef}
				height={height}
				noResultConfig={noResultConfig}
			>
				{i => (
					<Row
						key={i.id}
						selectable={isNotNil(selection) && selectionFor(i)}
						trackingId={i.id.toString()}
					>
						<Cell>{i.name}</Cell>
						{userColumnToShow === "all" && (
							<Cell>{i.numberOfUsers}</Cell>
						)}
						<Cell>{i.description}</Cell>
						{userColumnToShow === "active-direct" && (
							<Cell>{i.numberOfActiveNonDeletedDirectUsers}/{i.numberOfNonDeletedDirectUsers}</Cell>
						)}
					</Row>
				)}
			</TableView>
			<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
		</>

	);
};
UserGroupOverview.displayName = "UserGroupsOverview";

const notAvailableSelectionHandler = (selection: readonly UserGroupOverviewModel[]): void => {
	if (selection !== EMPTY_ARRAY) {
		console.error("Selection is not supported and ignored", selection);
	}
};

export const userGroupOverviewSearchPropertyKeys: readonly [LocalizationKey, ...LocalizationKey[]] = [
	"_Name",
	"_Description"
];
